import { inject, Injectable, OnDestroy, afterNextRender, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { SmoothScrollService, SmoothScrollOptions, EasingFn } from './smooth-scroll.service';

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly smooth = inject(SmoothScrollService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly destroy$ = new Subject<void>();

  private pendingScroll: { id: string; options: SmoothScrollOptions } | null = null;
  private lockActive = false;

  constructor() {
    if (this.isBrowser) {
      afterNextRender(() => {
        this.setupRouterSubscription();
      });
    }
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.pendingScroll) return;
        const { id, options } = this.pendingScroll;

        setTimeout(async () => {
          await this.smooth.scrollTo(`#${id}`, options);
          this.pinElementToViewport(`#${id}`, options.offset || 80);
          this.pendingScroll = null;
        }, 50);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.lockActive = false;
  }

  async navigateTo(
    route: string,
    options: {
      elementId?: string;
      offset?: number;
      duration?: number;
      block?: ScrollLogicalPosition;
      easing?: EasingFn;
      scrollBehavior?: ScrollBehavior;
    } = {}
  ): Promise<boolean> {
    const {
      elementId,
      offset = 80,
      duration = 700,
      block = 'start',
      easing,
      scrollBehavior = 'smooth',
    } = options;

    const animOptions: SmoothScrollOptions = {
      offset,
      duration: scrollBehavior === 'instant' ? 0 : duration,
      block,
      easing,
    };

    const currentBase = this.router.url.split('#')[0];
    if (currentBase === route) {
      if (elementId) {
        await this.smooth.scrollTo(`#${elementId}`, animOptions);
        this.pinElementToViewport(`#${elementId}`, offset);
      }
      return true;
    }

    if (elementId) {
      this.pendingScroll = {
        id: elementId,
        options: animOptions,
      };
    }

    return (await this.router.navigate([route])) as boolean;
  }

  async navigateToContactForm(serviceId?: string): Promise<boolean> {
    const queryParams = serviceId ? { service: serviceId } : {};

    await this.router.navigate([], {
      queryParams,
      queryParamsHandling: serviceId ? 'merge' : 'preserve',
      replaceUrl: true
    });

    const targetId = '#contact-us';
    const offset = 80;

    setTimeout(async () => {
      // 1. Умный скролл, который подстраивается под Elfsight в полете
      await this.smooth.scrollTo(targetId, { offset, duration: 700, block: 'start' });
      // 2. Жесткий якорь (невидимый для глаз пользователя)
      this.pinElementToViewport(targetId, offset);
    }, 50);

    return true;
  }

  /**
   * Жесткий якорь (Scroll Anchoring).
   * Если виджет Elfsight прогрузится УЖЕ ПОСЛЕ завершения анимации, 
   * он попытается оттолкнуть форму. Этот якорь мгновенно компенсирует сдвиг.
   */
  private pinElementToViewport(selector: string, targetOffset: number) {
    if (!this.isBrowser) return;

    const el = document.querySelector(selector) as HTMLElement;
    if (!el) return;

    this.lockActive = true;
    const duration = 3000; // Хватит 3 секунд удержания
    const startTime = Date.now();

    const cancelInteraction = () => {
      this.lockActive = false;
      window.removeEventListener('wheel', cancelInteraction);
      window.removeEventListener('touchstart', cancelInteraction);
    };

    // Если пользователь сам начал скроллить — отключаем якорь
    window.addEventListener('wheel', cancelInteraction, { passive: true });
    window.addEventListener('touchstart', cancelInteraction, { passive: true });

    const keepPinned = () => {
      if (!this.lockActive) return;

      if (Date.now() - startTime > duration) {
        cancelInteraction();
        return;
      }

      const currentTop = el.getBoundingClientRect().top;
      const diff = currentTop - targetOffset;

      // Если блок сдвинулся больше чем на 1px (Elfsight раздвинул DOM)
      if (Math.abs(diff) > 1) {
        // Мгновенный системный прыжок, чтобы глаз не заметил скачка формы
        window.scrollBy({ top: diff, behavior: 'instant' });
      }

      requestAnimationFrame(keepPinned);
    };

    requestAnimationFrame(keepPinned);
  }
}